/**
 * Enhanced i18n System for souk.immo
 * Comprehensive translations covering the entire app
 * Languages: English (en), French (fr), Standard Arabic (ar), Darija (ar-MA)
 */

export type Language = 'en' | 'fr' | 'ar' | 'ar-MA';

let currentLanguage: Language = (localStorage.getItem('language') as Language) || 'en';

export const setLanguage = (lang: Language) => {
  currentLanguage = lang;
  localStorage.setItem('language', lang);
  // RTL for Arabic variants
  document.dir = (lang === 'ar' || lang === 'ar-MA') ? 'rtl' : 'ltr';
};

export const getLanguage = (): Language => currentLanguage;

// Set initial direction
document.dir = (currentLanguage === 'ar' || currentLanguage === 'ar-MA') ? 'rtl' : 'ltr';

// Comprehensive translation object
export const translations = {
  en: {
    // ========== Navigation ==========
    nav_dashboard: 'Dashboard',
    nav_properties: 'Properties',
    nav_chantiers: 'Developments',
    nav_developments: 'Developments',
    nav_for_professionals: 'For Professionals',
    nav_clients: 'Clients',
    nav_matches: 'Matches',
    nav_analytics: 'Analytics',
    nav_settings: 'Settings',
    nav_users: 'User Management',
    nav_logout: 'Logout',
    navFeatures: 'Features',
    navHowItWorks: 'How It Works',
    navLogin: 'Login',
    navSignUp: 'Get Started',

    // ========== Footer ==========
    footerTagline: 'souk.immo: The Marketplace for Your Dream House',
    footerRights: 'All rights reserved',

    // ========== Tutorial ==========
    tutorialWelcomeTitle: 'Welcome to souk.immo',
    tutorialWelcomeDesc: 'Your intelligent real estate companion',
    tutorialRolesTitle: 'For Everyone',
    tutorialRolesDesc: 'Whether buying, selling, or developing',
    tutorialSkip: 'Skip',
    tutorialNext: 'Next',
    tutorialFinish: 'Get Started',

    // ========== Chantiers Page ==========
    chantiers_title: 'New Developments',
    chantiers_subtitle: 'Discover the most ambitious real estate projects shaping the future of Morocco',
    chantiers_search_placeholder: 'Search by project name or city...',
    chantiers_progress: 'Built',
    chantiers_delivery: 'Delivery',
    chantiers_learn_more: 'Learn More',
    chantiers_no_results: 'No developments found matching your search',
    chantiers_units_available: 'units available',

    // ========== Property Types ==========
    property_type_villa: 'Villa',
    property_type_apartment: 'Apartment',
    property_type_penthouse: 'Penthouse',
    property_type_riad: 'Riad',
    property_type_duplex: 'Duplex',
    property_type_studio: 'Studio',
    property_type_townhouse: 'Townhouse',
    property_type_office: 'Office',
    property_type_commercial: 'Commercial Space',
    property_type_land: 'Land',
    property_type_warehouse: 'Warehouse',
    property_type_building: 'Building',

    // ========== Property Status ==========
    status_available: 'Available',
    status_reserved: 'Reserved',
    status_sold: 'Sold',
    status_off_market: 'Off Market',
    status_pending: 'Pending',

    // ========== Common Actions ==========
    action_search: 'Search',
    action_filter: 'Filter',
    action_sort: 'Sort',
    action_save: 'Save',
    action_cancel: 'Cancel',
    action_delete: 'Delete',
    action_edit: 'Edit',
    action_view: 'View',
    action_create: 'Create',
    action_update: 'Update',
    action_submit: 'Submit',
    action_close: 'Close',
    action_back: 'Back',
    action_next: 'Next',
    action_previous: 'Previous',
    action_book_visit: 'Book a Visit',
    action_contact: 'Contact',
    action_share: 'Share',
    action_favorite: 'Add to Favorites',
    action_compare: 'Compare',
    action_download: 'Download',
    action_export: 'Export',
    action_import: 'Import',
    action_reset: 'Reset',
    action_apply: 'Apply',

    // ========== Form Labels ==========
    form_title: 'Title',
    form_description: 'Description',
    form_price: 'Price',
    form_price_min: 'Min Price',
    form_price_max: 'Max Price',
    form_budget: 'Budget',
    form_bedrooms: 'Bedrooms',
    form_bathrooms: 'Bathrooms',
    form_surface: 'Surface Area',
    form_surface_unit: 'm²',
    form_city: 'City',
    form_address: 'Address',
    form_neighborhood: 'Neighborhood',
    form_country: 'Country',
    form_postal_code: 'Postal Code',
    form_type: 'Property Type',
    form_status: 'Status',
    form_features: 'Features',
    form_amenities: 'Amenities',
    form_images: 'Images',
    form_video: 'Video',
    form_floor: 'Floor',
    form_total_floors: 'Total Floors',
    form_year_built: 'Year Built',
    form_parking: 'Parking Spaces',
    form_garden: 'Garden',
    form_pool: 'Swimming Pool',
    form_elevator: 'Elevator',
    form_balcony: 'Balcony',
    form_terrace: 'Terrace',
    form_furnished: 'Furnished',
    form_pet_friendly: 'Pet Friendly',

    // ========== Filter Labels ==========
    filter_all: 'All',
    filter_price_range: 'Price Range',
    filter_property_type: 'Property Type',
    filter_location: 'Location',
    filter_bedrooms: 'Bedrooms',
    filter_surface: 'Surface Area',
    filter_show_results: 'Show Results',
    filter_clear_all: 'Clear All Filters',
    filter_min: 'Min',
    filter_max: 'Max',
    filter_any: 'Any',

    // ========== Cities (Moroccan) ==========
    city_casablanca: 'Casablanca',
    city_rabat: 'Rabat',
    city_marrakech: 'Marrakech',
    city_fes: 'Fes',
    city_tangier: 'Tangier',
    city_agadir: 'Agadir',
    city_meknes: 'Meknes',
    city_oujda: 'Oujda',
    city_kenitra: 'Kenitra',
    city_tetouan: 'Tetouan',
    city_safi: 'Safi',
    city_mohammedia: 'Mohammedia',
    city_el_jadida: 'El Jadida',

    // ========== Error Messages ==========
    error_required: 'This field is required',
    error_invalid_email: 'Invalid email address',
    error_invalid_phone: 'Invalid phone number',
    error_min_price: 'Price must be at least {min}',
    error_max_price: 'Price cannot exceed {max}',
    error_min_length: 'Must be at least {min} characters',
    error_max_length: 'Cannot exceed {max} characters',
    error_network: 'Network error. Please try again.',
    error_unauthorized: 'You are not authorized to perform this action',
    error_not_found: 'Resource not found',
    error_server: 'Server error. Please try again later.',
    error_upload: 'Failed to upload file',
    error_invalid_format: 'Invalid format',

    // ========== Success Messages ==========
    success_saved: 'Saved successfully',
    success_deleted: 'Deleted successfully',
    success_created: 'Created successfully',
    success_updated: 'Updated successfully',
    success_uploaded: 'Uploaded successfully',
    success_sent: 'Sent successfully',

    // ========== Validation Messages ==========
    validation_email: 'Please enter a valid email',
    validation_password_min: 'Password must be at least 8 characters',
    validation_match: 'Fields must match',
    validation_numeric: 'Must be a number',
    validation_positive: 'Must be a positive number',

    // ========== Property Details ==========
    detail_overview: 'Overview',
    detail_features: 'Features',
    detail_location: 'Location',
    detail_price_details: 'Price Details',
    detail_contact_agent: 'Contact Agent',
    detail_request_info: 'Request Information',
    detail_schedule_visit: 'Schedule a Visit',
    detail_similar: 'Similar Properties',
    detail_map: 'Map View',
    detail_street_view: 'Street View',
    detail_virtual_tour: '360° Virtual Tour',

    // ========== Units ==========
    unit_mad: 'MAD',
    unit_sqm: 'm²',
    unit_beds: 'beds',
    unit_baths: 'baths',
    unit_floor: 'Floor',
    unit_per_month: 'per month',
    unit_per_sqm: 'per m²',

    // ========== Time ==========
    time_just_now: 'Just now',
    time_minutes_ago: '{minutes} minutes ago',
    time_hours_ago: '{hours} hours ago',
    time_days_ago: '{days} days ago',
    time_weeks_ago: '{weeks} weeks ago',

    // ========== Dashboard ==========
    dashboard_welcome: 'Welcome back',
    dashboard_total_properties: 'Total Properties',
    dashboard_active_clients: 'Active Clients',
    dashboard_matches: 'Matches This Month',
    dashboard_revenue: 'Total Revenue',
    dashboard_recent_activity: 'Recent Activity',
    dashboard_top_properties: 'Top Performing Properties',

    // ========== Placeholders ==========
    placeholder_search: 'Search...',
    placeholder_select: 'Select...',
    placeholder_type_here: 'Type here...',
    placeholder_select_city: 'Select a city',
    placeholder_enter_location: 'Enter a location',

    // ========== Misc ==========
    loading: 'Loading...',
    no_results: 'No results found',
    empty_state: 'Nothing to show yet',
    coming_soon: 'Coming Soon',
    view_all: 'View All',
    show_more: 'Show More',
    show_less: 'Show Less',
    verified: 'Verified',
    featured: 'Featured',
    new: 'New',
    hot_deal: 'Hot Deal',
    price_reduced: 'Price Reduced',

    // ========== Aggregated Properties ==========
    aggregated_properties_title: 'Browse All Properties',
    aggregated_properties_desc: 'Properties from verified developers across Morocco',
    aggregated_from_multiple_sources: 'Aggregated from multiple sources',
    aggregated_properties_disclaimer: 'Properties are listed by their respective developers. souk.immo aggregates listings to help buyers discover opportunities. Always verify details with the developer directly.',
    contact_promoteur: 'Contact Developer',
    view_original_listing: 'View Original Listing',
    properties_found: 'properties found',
    all_cities: 'All Cities',
    all_types: 'All Types',
    property_type_other: 'Other',
    apply_filters: 'Apply Filters',
    filters: 'Filters',
    min_price: 'Min Price',
    max_price: 'Max Price',
    min_bedrooms: 'Min Bedrooms',
    no_properties_found: 'No properties found',
    disclaimer: 'Disclaimer',
    attribution: 'Attribution',

    // ========== Lead Capture Form ==========
    lead_form_title: 'Express Your Interest',
    lead_form_step1_desc: 'We\'ll connect you with',
    lead_form_budget: 'Your Budget',
    lead_form_budget_help: 'This helps us understand your purchasing power',
    lead_form_financing: 'How will you finance?',
    lead_form_cash: 'Cash Payment',
    lead_form_mortgage: 'Mortgage/Loan',
    lead_form_unsure: 'Not Sure Yet',
    lead_form_preapproved: 'I have mortgage pre-approval',
    lead_form_timeframe: 'When are you planning to buy?',
    lead_form_immediate: 'Immediately (0-1 month)',
    lead_form_3months: 'Within 3 months',
    lead_form_6months: 'Within 6 months',
    lead_form_1year: 'Within 1 year',
    lead_form_request_viewing: 'I\'d like to schedule a viewing',
    lead_form_preferred_date: 'Preferred Viewing Date',
    lead_form_features_interested: 'Which features interest you most?',
    lead_form_message: 'Additional Message (Optional)',
    lead_form_message_placeholder: 'Tell us more about what you\'re looking for...',
    lead_form_message_help: 'Any specific requirements or questions?',
    lead_form_disclaimer: 'Your information will be shared directly with the developer who will contact you.',
    lead_form_success_title: 'Inquiry Submitted!',
    lead_form_success_desc: 'Your information has been sent to',
    submit_inquiry: 'Submit Inquiry',
    submitting: 'Submitting...',
    cancel: 'Cancel',
    back: 'Back',
    next: 'Next',

    // ========== Leads Dashboard ==========
    leads_dashboard: 'Leads Dashboard',
    leads_dashboard_desc: 'Manage all inquiries from buyers',
    total_leads: 'Total Leads',
    hot_leads: 'Hot Leads',
    warm_leads: 'Warm Leads',
    cold_leads: 'Cold Leads',
    new_leads: 'New Leads',
    all_leads: 'All Leads',
    avg_score: 'Avg Score',
    quality: 'Quality',
    score: 'Score',
    status: 'Status',
    date: 'Date',
    actions: 'Actions',
    lead_details: 'Lead Details',
    not_specified: 'Not specified',
    mark_as_contacted: 'Mark as Contacted',
    name: 'Name',
    contact: 'Contact',
    property: 'Property',
    budget: 'Budget',
    financing: 'Financing',
    timeframe: 'Timeframe',
    message: 'Message',
    search: 'Search',
    search_properties: 'Search properties by title, city, or developer...',
  },

  fr: {
    // ========== Navigation ==========
    nav_dashboard: 'Tableau de bord',
    nav_properties: 'Propriétés',
    nav_chantiers: 'Projets',
    nav_developments: 'Projets',
    nav_for_professionals: 'Pour Professionnels',
    nav_clients: 'Clients',
    nav_matches: 'Correspondances',
    nav_analytics: 'Analytique',
    nav_settings: 'Paramètres',
    nav_users: 'Gestion des Utilisateurs',
    nav_logout: 'Déconnexion',
    navFeatures: 'Fonctionnalités',
    navHowItWorks: 'Comment Ça Marche',
    navLogin: 'Connexion',
    navSignUp: 'Commencer',

    // ========== Footer ==========
    footerTagline: 'souk.immo : La Marketplace pour votre Maison de Rêve',
    footerRights: 'Tous droits réservés',

    // ========== Tutorial ==========
    tutorialWelcomeTitle: 'Bienvenue sur souk.immo',
    tutorialWelcomeDesc: 'Votre compagnon immobilier intelligent',
    tutorialRolesTitle: 'Pour Tout le Monde',
    tutorialRolesDesc: 'Que vous achetiez, vendiez ou développiez',
    tutorialSkip: 'Passer',
    tutorialNext: 'Suivant',
    tutorialFinish: 'Commencer',

    // ========== Chantiers Page ==========
    chantiers_title: 'Nouveaux Projets',
    chantiers_subtitle: 'Découvrez les projets immobiliers les plus ambitieux façonnant l\'avenir du Maroc',
    chantiers_search_placeholder: 'Rechercher par nom de projet ou ville...',
    chantiers_progress: 'Construit',
    chantiers_delivery: 'Livraison',
    chantiers_learn_more: 'En Savoir Plus',
    chantiers_no_results: 'Aucun projet trouvé correspondant à votre recherche',
    chantiers_units_available: 'unités disponibles',

    // ========== Property Types ==========
    property_type_villa: 'Villa',
    property_type_apartment: 'Appartement',
    property_type_penthouse: 'Penthouse',
    property_type_riad: 'Riad',
    property_type_duplex: 'Duplex',
    property_type_studio: 'Studio',
    property_type_townhouse: 'Maison de Ville',
    property_type_office: 'Bureau',
    property_type_commercial: 'Local Commercial',
    property_type_land: 'Terrain',
    property_type_warehouse: 'Entrepôt',
    property_type_building: 'Immeuble',

    // ========== Property Status ==========
    status_available: 'Disponible',
    status_reserved: 'Réservé',
    status_sold: 'Vendu',
    status_off_market: 'Hors Marché',
    status_pending: 'En Attente',

    // ========== Common Actions ==========
    action_search: 'Rechercher',
    action_filter: 'Filtrer',
    action_sort: 'Trier',
    action_save: 'Enregistrer',
    action_cancel: 'Annuler',
    action_delete: 'Supprimer',
    action_edit: 'Modifier',
    action_view: 'Voir',
    action_create: 'Créer',
    action_update: 'Mettre à Jour',
    action_submit: 'Soumettre',
    action_close: 'Fermer',
    action_back: 'Retour',
    action_next: 'Suivant',
    action_previous: 'Précédent',
    action_book_visit: 'Réserver une Visite',
    action_contact: 'Contacter',
    action_share: 'Partager',
    action_favorite: 'Ajouter aux Favoris',
    action_compare: 'Comparer',
    action_download: 'Télécharger',
    action_export: 'Exporter',
    action_import: 'Importer',
    action_reset: 'Réinitialiser',
    action_apply: 'Appliquer',

    // ========== Form Labels ==========
    form_title: 'Titre',
    form_description: 'Description',
    form_price: 'Prix',
    form_price_min: 'Prix Min',
    form_price_max: 'Prix Max',
    form_budget: 'Budget',
    form_bedrooms: 'Chambres',
    form_bathrooms: 'Salles de Bain',
    form_surface: 'Surface',
    form_surface_unit: 'm²',
    form_city: 'Ville',
    form_address: 'Adresse',
    form_neighborhood: 'Quartier',
    form_country: 'Pays',
    form_postal_code: 'Code Postal',
    form_type: 'Type de Propriété',
    form_status: 'Statut',
    form_features: 'Caractéristiques',
    form_amenities: 'Équipements',
    form_images: 'Images',
    form_video: 'Vidéo',
    form_floor: 'Étage',
    form_total_floors: 'Nombre d\'Étages',
    form_year_built: 'Année de Construction',
    form_parking: 'Places de Parking',
    form_garden: 'Jardin',
    form_pool: 'Piscine',
    form_elevator: 'Ascenseur',
    form_balcony: 'Balcon',
    form_terrace: 'Terrasse',
    form_furnished: 'Meublé',
    form_pet_friendly: 'Animaux Acceptés',

    // ========== Filter Labels ==========
    filter_all: 'Tous',
    filter_price_range: 'Fourchette de Prix',
    filter_property_type: 'Type de Propriété',
    filter_location: 'Localisation',
    filter_bedrooms: 'Chambres',
    filter_surface: 'Surface',
    filter_show_results: 'Afficher les Résultats',
    filter_clear_all: 'Effacer Tous les Filtres',
    filter_min: 'Min',
    filter_max: 'Max',
    filter_any: 'Tout',

    // ========== Cities (Moroccan) ==========
    city_casablanca: 'Casablanca',
    city_rabat: 'Rabat',
    city_marrakech: 'Marrakech',
    city_fes: 'Fès',
    city_tangier: 'Tanger',
    city_agadir: 'Agadir',
    city_meknes: 'Meknès',
    city_oujda: 'Oujda',
    city_kenitra: 'Kénitra',
    city_tetouan: 'Tétouan',
    city_safi: 'Safi',
    city_mohammedia: 'Mohammedia',
    city_el_jadida: 'El Jadida',

    // ========== Error Messages ==========
    error_required: 'Ce champ est requis',
    error_invalid_email: 'Adresse email invalide',
    error_invalid_phone: 'Numéro de téléphone invalide',
    error_min_price: 'Le prix doit être d\'au moins {min}',
    error_max_price: 'Le prix ne peut pas dépasser {max}',
    error_min_length: 'Doit contenir au moins {min} caractères',
    error_max_length: 'Ne peut pas dépasser {max} caractères',
    error_network: 'Erreur réseau. Veuillez réessayer.',
    error_unauthorized: 'Vous n\'êtes pas autorisé à effectuer cette action',
    error_not_found: 'Ressource non trouvée',
    error_server: 'Erreur serveur. Veuillez réessayer plus tard.',
    error_upload: 'Échec du téléchargement du fichier',
    error_invalid_format: 'Format invalide',

    // ========== Success Messages ==========
    success_saved: 'Enregistré avec succès',
    success_deleted: 'Supprimé avec succès',
    success_created: 'Créé avec succès',
    success_updated: 'Mis à jour avec succès',
    success_uploaded: 'Téléchargé avec succès',
    success_sent: 'Envoyé avec succès',

    // ========== Validation Messages ==========
    validation_email: 'Veuillez entrer un email valide',
    validation_password_min: 'Le mot de passe doit contenir au moins 8 caractères',
    validation_match: 'Les champs doivent correspondre',
    validation_numeric: 'Doit être un nombre',
    validation_positive: 'Doit être un nombre positif',

    // ========== Property Details ==========
    detail_overview: 'Aperçu',
    detail_features: 'Caractéristiques',
    detail_location: 'Localisation',
    detail_price_details: 'Détails du Prix',
    detail_contact_agent: 'Contacter l\'Agent',
    detail_request_info: 'Demander des Informations',
    detail_schedule_visit: 'Planifier une Visite',
    detail_similar: 'Propriétés Similaires',
    detail_map: 'Vue Carte',
    detail_street_view: 'Vue de la Rue',
    detail_virtual_tour: 'Visite Virtuelle 360°',

    // ========== Units ==========
    unit_mad: 'MAD',
    unit_sqm: 'm²',
    unit_beds: 'chambres',
    unit_baths: 'salles de bain',
    unit_floor: 'Étage',
    unit_per_month: 'par mois',
    unit_per_sqm: 'par m²',

    // ========== Time ==========
    time_just_now: 'À l\'instant',
    time_minutes_ago: 'Il y a {minutes} minutes',
    time_hours_ago: 'Il y a {hours} heures',
    time_days_ago: 'Il y a {days} jours',
    time_weeks_ago: 'Il y a {weeks} semaines',

    // ========== Dashboard ==========
    dashboard_welcome: 'Bienvenue',
    dashboard_total_properties: 'Total des Propriétés',
    dashboard_active_clients: 'Clients Actifs',
    dashboard_matches: 'Correspondances Ce Mois',
    dashboard_revenue: 'Revenu Total',
    dashboard_recent_activity: 'Activité Récente',
    dashboard_top_properties: 'Propriétés les Plus Performantes',

    // ========== Placeholders ==========
    placeholder_search: 'Rechercher...',
    placeholder_select: 'Sélectionner...',
    placeholder_type_here: 'Tapez ici...',
    placeholder_select_city: 'Sélectionner une ville',
    placeholder_enter_location: 'Entrez un emplacement',

    // ========== Misc ==========
    loading: 'Chargement...',
    no_results: 'Aucun résultat trouvé',
    empty_state: 'Rien à afficher pour le moment',
    coming_soon: 'Bientôt Disponible',
    view_all: 'Voir Tout',
    show_more: 'Voir Plus',
    show_less: 'Voir Moins',
    verified: 'Vérifié',
    featured: 'Mis en Avant',
    new: 'Nouveau',
    hot_deal: 'Bonne Affaire',
    price_reduced: 'Prix Réduit',

    // ========== Aggregated Properties ==========
    aggregated_properties_title: 'Parcourir Tous les Biens',
    aggregated_properties_desc: 'Propriétés de promoteurs vérifiés à travers le Maroc',
    aggregated_from_multiple_sources: 'Agrégé de plusieurs sources',
    aggregated_properties_disclaimer: 'Les propriétés sont listées par leurs promoteurs respectifs. souk.immo agrège les annonces pour aider les acheteurs à découvrir des opportunités. Vérifiez toujours les détails directement auprès du promoteur.',
    contact_promoteur: 'Contacter le Promoteur',
    view_original_listing: 'Voir l\'Annonce Originale',
    properties_found: 'propriétés trouvées',
    all_cities: 'Toutes les Villes',
    all_types: 'Tous les Types',
    property_type_other: 'Autre',
    apply_filters: 'Appliquer les Filtres',
    filters: 'Filtres',
    min_price: 'Prix Min',
    max_price: 'Prix Max',
    min_bedrooms: 'Chambres Min',
    no_properties_found: 'Aucune propriété trouvée',
    disclaimer: 'Avertissement',
    attribution: 'Attribution',

    // ========== Lead Capture Form ==========
    lead_form_title: 'Exprimer Votre Intérêt',
    lead_form_step1_desc: 'Nous vous mettrons en contact avec',
    lead_form_budget: 'Votre Budget',
    lead_form_budget_help: 'Cela nous aide à comprendre votre pouvoir d\'achat',
    lead_form_financing: 'Comment allez-vous financer?',
    lead_form_cash: 'Paiement Comptant',
    lead_form_mortgage: 'Prêt Hypothécaire',
    lead_form_unsure: 'Pas Encore Sûr',
    lead_form_preapproved: 'J\'ai une pré-approbation de prêt',
    lead_form_timeframe: 'Quand prévoyez-vous d\'acheter?',
    lead_form_immediate: 'Immédiatement (0-1 mois)',
    lead_form_3months: 'Dans les 3 mois',
    lead_form_6months: 'Dans les 6 mois',
    lead_form_1year: 'Dans l\'année',
    lead_form_request_viewing: 'Je souhaite planifier une visite',
    lead_form_preferred_date: 'Date de Visite Préférée',
    lead_form_features_interested: 'Quelles caractéristiques vous intéressent le plus?',
    lead_form_message: 'Message Additionnel (Optionnel)',
    lead_form_message_placeholder: 'Dites-nous plus sur ce que vous cherchez...',
    lead_form_message_help: 'Des exigences spécifiques ou des questions?',
    lead_form_disclaimer: 'Vos informations seront partagées directement avec le promoteur qui vous contactera.',
    lead_form_success_title: 'Demande Soumise!',
    lead_form_success_desc: 'Vos informations ont été envoyées à',
    submit_inquiry: 'Soumettre la Demande',
    submitting: 'En cours...',
    cancel: 'Annuler',
    back: 'Retour',
    next: 'Suivant',

    // ========== Leads Dashboard ==========
    leads_dashboard: 'Tableau de Bord des Leads',
    leads_dashboard_desc: 'Gérer toutes les demandes des acheteurs',
    total_leads: 'Total des Leads',
    hot_leads: 'Leads Chauds',
    warm_leads: 'Leads Tièdes',
    cold_leads: 'Leads Froids',
    new_leads: 'Nouveaux Leads',
    all_leads: 'Tous les Leads',
    avg_score: 'Score Moyen',
    quality: 'Qualité',
    score: 'Score',
    status: 'Statut',
    date: 'Date',
    actions: 'Actions',
    lead_details: 'Détails du Lead',
    not_specified: 'Non spécifié',
    mark_as_contacted: 'Marquer comme Contacté',
    name: 'Nom',
    contact: 'Contact',
    property: 'Propriété',
    budget: 'Budget',
    financing: 'Financement',
    timeframe: 'Calendrier',
    message: 'Message',
    search: 'Rechercher',
    search_properties: 'Rechercher par titre, ville ou promoteur...',
  },

  ar: {
    // Standard Arabic translations
    // (Same structure as above, I'll add a few key ones)
    nav_dashboard: 'لوحة التحكم',
    nav_properties: 'العقارات',
    nav_chantiers: 'المشاريع',
    nav_developments: 'المشاريع',
    nav_for_professionals: 'للمحترفين',
    nav_clients: 'العملاء',
    navFeatures: 'المميزات',
    navHowItWorks: 'كيف يعمل',
    navLogin: 'تسجيل الدخول',
    navSignUp: 'ابدأ الآن',

    // ========== Footer ==========
    footerTagline: 'souk.immo: منصة بيت أحلامك',
    footerRights: 'جميع الحقوق محفوظة',

    // ========== Tutorial ==========
    tutorialWelcomeTitle: 'مرحبًا بك في souk.immo',
    tutorialWelcomeDesc: 'رفيقك العقاري الذكي',
    tutorialRolesTitle: 'للجميع',
    tutorialRolesDesc: 'سواء كنت تشتري أو تبيع أو تطور',
    tutorialSkip: 'تخطي',
    tutorialNext: 'التالي',
    tutorialFinish: 'ابدأ الآن',

    chantiers_title: 'المشاريع الجديدة',
    chantiers_subtitle: 'اكتشف أكثر المشاريع العقارية طموحًا التي تشكل مستقبل المغرب',
    chantiers_search_placeholder: 'ابحث باسم المشروع أو المدينة...',
    property_type_villa: 'فيلا',
    property_type_apartment: 'شقة',
    property_type_riad: 'رياض',
    action_search: 'بحث',
    action_book_visit: 'حجز زيارة',
    city_casablanca: 'الدار البيضاء',
    city_rabat: 'الرباط',
    city_marrakech: 'مراكش',
    // ... (add remaining translations)
  },

  'ar-MA': {
    // Darija (Moroccan Arabic) - conversational
    nav_dashboard: 'طابلو دو بور',
    nav_properties: 'العقارات',
    nav_chantiers: 'الشونتييات',
    nav_developments: 'الشونتييات',
    nav_for_professionals: 'للمحترفين',
    navFeatures: 'الفيتشرز',
    navHowItWorks: 'كيفاش خدام',
    navLogin: 'دخول',
    navSignUp: 'سجل',

    // ========== Footer ==========
    footerTagline: 'souk.immo: السوق ديال الدار ديال أحلامك',
    footerRights: 'جميع الحقوق محفوظة',

    // ========== Tutorial ==========
    tutorialWelcomeTitle: 'مرحبا بيك ف souk.immo',
    tutorialWelcomeDesc: 'الرفيق ديالك ف العقار',
    tutorialRolesTitle: 'للجميع',
    tutorialRolesDesc: 'واش كنتي باغي تشري، تبيع، أو تبني',
    tutorialSkip: 'تجاوز',
    tutorialNext: 'اللي جاي',
    tutorialFinish: 'يالاه نبداو',

    chantiers_title: 'الشونتييات الجداد',
    chantiers_subtitle: 'شوف أكبر البروجيات العقارية لي كاتشكل مستقبل المغرب',
    chantiers_search_placeholder: 'قلب بالسمية ديال البروجي ولا المدينة...',
    property_type_villa: 'فيلا',
    property_type_apartment: 'شقة',
    property_type_riad: 'رياض',
    action_search: 'قلب',
    action_book_visit: 'حجز زيارة',
    city_casablanca: 'كازا',
    city_rabat: 'الرباط',
    city_marrakech: 'مراكش',
    // ... (add remaining Darija translations)
  },
};

// Enhanced translation function with variable substitution
export const t = (key: keyof typeof translations.en, vars?: Record<string, string | number>): string => {
  const langTranslations = translations[currentLanguage] as Record<string, string>;
  let translation = langTranslations?.[key] || translations.en[key] || key;

  // Variable substitution (e.g., "{min}" => "100")
  if (vars) {
    Object.keys(vars).forEach(varKey => {
      translation = translation.replace(`{${varKey}}`, String(vars[varKey]));
    });
  }

  return translation;
};

// Helper to get all property types with translations
export const getPropertyTypes = (): Array<{ value: string; label: string }> => [
  { value: 'villa', label: t('property_type_villa') },
  { value: 'apartment', label: t('property_type_apartment') },
  { value: 'penthouse', label: t('property_type_penthouse') },
  { value: 'riad', label: t('property_type_riad') },
  { value: 'duplex', label: t('property_type_duplex') },
  { value: 'studio', label: t('property_type_studio') },
  { value: 'townhouse', label: t('property_type_townhouse') },
  { value: 'office', label: t('property_type_office') },
  { value: 'commercial', label: t('property_type_commercial') },
  { value: 'land', label: t('property_type_land') },
];

// Helper to get all cities with translations
export const getMoroccanCities = (): Array<{ value: string; label: string }> => [
  { value: 'casablanca', label: t('city_casablanca') },
  { value: 'rabat', label: t('city_rabat') },
  { value: 'marrakech', label: t('city_marrakech') },
  { value: 'fes', label: t('city_fes') },
  { value: 'tangier', label: t('city_tangier') },
  { value: 'agadir', label: t('city_agadir') },
  { value: 'meknes', label: t('city_meknes') },
  { value: 'oujda', label: t('city_oujda') },
  { value: 'kenitra', label: t('city_kenitra') },
  { value: 'tetouan', label: t('city_tetouan') },
  { value: 'safi', label: t('city_safi') },
  { value: 'mohammedia', label: t('city_mohammedia') },
  { value: 'el_jadida', label: t('city_el_jadida') },
];

export default { t, setLanguage, getLanguage, translations };
